import {connect} from 'react-redux';

import {ConversationActionContextsEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {SendActionsEnum} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  makeGetAttachments,
  makeGetReferencedActivity,
  makeGetReferencedAuthor,
  makeGetReferencedComment,
  makeGetReferencedRecipient,
} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {ActivityVisualTypesEnum} from '../../../../../models/api/activityModel';
import {CommentModel, CommentStatusesEnum} from '../../../../../models/api/commentModel';
import {AppState} from '../../../../../store/appState';
import {deleteSendingCommentAsyncAction} from '../../../../../store/data/comments/commentsActions';
import {saveCommentAsyncAction} from '../../../../../store/data/comments/saveCommentActions';
import {sendCommentAsyncAction} from '../../../../../store/data/comments/sendCommentAction';
import {getComments, getConversations} from '../../../../../store/data/dataSelectors';
import {getMentionableAliases} from '../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUser, getUserAlias, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {CommentViewer} from '../comment/commentViewer';
import {SendingActivityViewerProps} from '../sendingActivityViewer';

/*
 * Props.
 */

interface SendingCommentViewerContainerProps extends SendingActivityViewerProps {
  cellsContext: CellsContext;
  comment: CommentModel;
  conversationActionContext: ConversationActionContextsEnum | undefined;
}

/*
 * Selectors.
 */

/** Extract the sending comment from the props. */
function getComment(_state: AppState, props: SendingCommentViewerContainerProps) {
  return props.comment;
}

/** Extract the previous sending activity from the props. */
function getPreviousSendingActivity(_state: AppState, props: SendingCommentViewerContainerProps) {
  return props.previousSendingActivity;
}

/** Extract the next sending activity from the props. */
function getNextSendingActivity(_state: AppState, props: SendingCommentViewerContainerProps) {
  return props.nextSendingActivity;
}

/** Extract the last timeline activity from the props. */
function getLastCommentActivity(_state: AppState, props: SendingCommentViewerContainerProps) {
  const {lastActivity} = props;
  if (!lastActivity) {
    return undefined;
  }

  if (lastActivity.visualType !== ActivityVisualTypesEnum.COMMENT) {
    return undefined;
  }

  return lastActivity;
}

function makeGetConversation() {
  return createSelector([getComment, getConversations], (comment, conversations) => {
    const conversation = conversations[comment.conversationId];
    if (!conversation) {
      throw new Error('Could not find conversation.');
    }

    return conversation;
  });
}

/** Find the comment for the last timeline activity, if any. */
function makeGetLastActivityComment() {
  return createSelector(
    [getLastCommentActivity, getComments],
    (activity, comments) => activity && comments[activity.commentUid],
  );
}

/** Find if this sending activity has the same author as the previous. */
function makeGetHasSameAuthorAsPrevious() {
  return createSelector(
    [getComment, getPreviousSendingActivity, makeGetLastActivityComment()],
    (comment, previousSendingActivity, lastActivityComment) => {
      // If there's a previous sending activity, compare its comment.
      if (previousSendingActivity) {
        const comment2 = previousSendingActivity.comment;
        return Boolean(comment2 && comment2.authorId === comment.authorId);
      }

      // Otherwise, compare the last timeline comment.
      return Boolean(lastActivityComment && lastActivityComment.authorId === comment.authorId);
    },
  );
}

function makeGetIsReferencingTheSameMessageAsPrevious() {
  return createSelector(
    [getComment, getPreviousSendingActivity, makeGetLastActivityComment()],
    (comment, previousSendingActivity, lastActivityComment) => {
      // If there's a previous sending activity, compare its comment.
      if (previousSendingActivity) {
        const comment2 = previousSendingActivity.comment;
        return (
          Boolean(comment2 && comment2.referencedActivityId === comment.referencedActivityId) &&
          Boolean(comment2?.annotationUid) &&
          Boolean(comment.annotationUid)
        );
      }

      // Otherwise, compare the last timeline comment.
      return (
        Boolean(
          lastActivityComment && lastActivityComment.referencedActivityId === comment.referencedActivityId,
        ) &&
        Boolean(lastActivityComment?.annotationUid) &&
        Boolean(comment.annotationUid)
      );
    },
  );
}

/** Find if this comment is a last consecutive sending comment. */
function makeGetIsLastSendingComment() {
  return createSelector(
    [getComment, getNextSendingActivity, makeGetLastActivityComment()],
    (comment, nextSendingActivity, lastActivityComment) => {
      // It's not the last sending if this message isn't sending or pending.
      if (
        !(comment.status === CommentStatusesEnum.SENDING || comment.status === CommentStatusesEnum.PENDING)
      ) {
        return false;
      }

      // It's the last if there's not another comment next.
      if (!nextSendingActivity || !nextSendingActivity.comment) {
        return true;
      }

      // It's not the last if the next comment is pending/sending.
      if (
        nextSendingActivity.comment.status === CommentStatusesEnum.PENDING ||
        nextSendingActivity.comment.status === CommentStatusesEnum.SENDING
      ) {
        return false;
      }

      return true;
    },
  );
}

/** A comment that's in the sending state cannot have annotations yet (comments which quote it). So we NOP. */
function getAnnotations() {
  return [];
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SendingCommentViewerContainerProps>()(() => {
  const getReferencedActivity = makeGetReferencedActivity(getComment);
  return {
    author: getUser,
    attachments: makeGetAttachments(getComment),
    annotations: getAnnotations,
    conversation: makeGetConversation(),
    hasSameAuthorAsPrevious: makeGetHasSameAuthorAsPrevious(),
    isReferencingTheSameMessageAsPrevious: makeGetIsReferencingTheSameMessageAsPrevious(),
    isLastSendingComment: makeGetIsLastSendingComment(),
    mentionableAliases: getMentionableAliases,
    userAlias: getUserAlias,
    referencedAuthor: makeGetReferencedAuthor(getReferencedActivity),
    referencedComment: makeGetReferencedComment(getReferencedActivity),
    referencedRecipient: makeGetReferencedRecipient(getReferencedActivity),
    userId: getUserId,
    emojiValidationMap: getEmojiValidationMap,
  };
});

export const SendingCommentViewerContainer = connect(
  mapStateToProps,
  {
    saveCommentAsync: saveCommentAsyncAction,
    sendCommentAsync: sendCommentAsyncAction,
    deleteCommentAsync: deleteSendingCommentAsyncAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {panelId, comment, conversationId, cellsContext} = ownProps;
    return {
      ...ownProps,
      ...stateProps,
      isFromUser: true,
      retryComment: () => {
        dispatchProps.saveCommentAsync(comment, {panelId});
        dispatchProps.sendCommentAsync({
          cellsContext,
          conversationId,
          commentUid: comment.uid,
          ignoreWarnings: true,
          sendAction: SendActionsEnum.SEND,
        });
      },
      deleteComment: () => {
        dispatchProps.deleteCommentAsync(comment.uid);
      },
    };
  },
)(CommentViewer);
